import {
  AccountCircleOutlined,
  Assessment,
  AssignmentInd,
  CampaignOutlined,
  CompareArrowsOutlined,
  DevicesOther,
  Email,
  Extension,
  ForwardToInboxOutlined,
  GroupsOutlined,
  HeadsetMicOutlined,
  Inventory2Outlined,
  LockOutlined,
  NotificationsOutlined,
  PhoneOutlined,
  RocketLaunch,
  School,
  Settings,
  ShoppingCartOutlined,
  TrendingUp,
  VideoLibrary,
} from "@mui/icons-material";
import { Box, Breadcrumbs, Link, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { useCanAccess, useCanAccessResources, useGetOne, useTranslate } from "react-admin";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";

import { CrmObjectIcon } from "../components/crm/CrmObjectIcon";
import { Icon } from "../components/icons";
import {
  BILLWERK_CUSTOMER_MATCH_ROUTE,
  BILLWERK_PLAN_VARIANTS_ROUTE,
  CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE,
  FINANCE_NOTIFICATIONS_ROUTE,
  FINANCE_OVERVIEW_ROUTE,
  LOGISTICS_INVENTORY_ITEM_ROUTE,
  LOGISTICS_INVENTORY_ROUTE,
  ResourceName,
  SALESFORCE_DASHBOARD_ROUTE,
  SALESFORCE_RECORDS_ROUTE,
} from "../constants/resources";
import type { CrmObject } from "../generated/graphql";
import { useCrmLabels } from "../hooks/useCrmLabels";
import { useCustomerServiceLearningOrganizations } from "../hooks/useCustomerServiceLearningOrganizations";
import {
  CRM_CALLS_ROUTE,
  CRM_DUPLICATES_ROUTE,
  CRM_RECORD_ROUTE,
  CRM_ROUTE,
  crmListPath,
  crmObjectFromSlug,
} from "../lib/crm";
import {
  CUSTOMER_SERVICE_ORGANIZATION_ROUTE,
  CUSTOMER_SERVICE_ORGANIZATIONS_ROUTE,
  customerServiceOrganizationPath,
} from "../lib/customerServiceOrganizationRoutes";
import { formatName } from "../lib/formatName";
import { MARKETING_ROUTE, WEB_ORDERS_ROUTE } from "../lib/marketing";
import { PROCEDURE_CLIENT_QUERY_PARAM } from "../lib/procedureRoutes";
import { maxWidth, space } from "../theme/designTokens";
import type { ProcedureRecord } from "../types/procedure";
import { useBreadcrumbTitle } from "./BreadcrumbTitleContext";

const ANALYTICS_ROUTE = "/analytics";

// The analytics pages, labelled as in the sidebar.
const ANALYTICS_PAGES: AnalyticsPage[] = [
  { segment: "trends", labelKey: "analytics.trends", icon: <TrendingUp fontSize="small" /> },
  { segment: "features", labelKey: "analytics.features", icon: <Extension fontSize="small" /> },
  { segment: "distributions", labelKey: "analytics.distributions", icon: <DevicesOther fontSize="small" /> },
  { segment: "content", labelKey: "analytics.contentUsage.menu", icon: <VideoLibrary fontSize="small" /> },
];

interface AnalyticsPage {
  segment: string;
  labelKey: string;
  icon: React.ReactNode;
}

// Logistics pages in sidebar order: the section links to the first one the user can open.
const LOGISTICS_PAGES = [{ resource: ResourceName.LogisticsInventory, to: LOGISTICS_INVENTORY_ROUTE }];

// Finance pages in sidebar order: the section links to the first one the user can open.
const FINANCE_PAGES = [
  { resource: ResourceName.FinanceOverview, to: FINANCE_OVERVIEW_ROUTE },
  { resource: ResourceName.FinanceNotifications, to: FINANCE_NOTIFICATIONS_ROUTE },
  { resource: ResourceName.BillwerkCustomerMatch, to: BILLWERK_CUSTOMER_MATCH_ROUTE },
  { resource: ResourceName.BillwerkPlanVariants, to: BILLWERK_PLAN_VARIANTS_ROUTE },
];

// MUI collapses a trail only when it is longer than maxItems, which must exceed the items kept at both ends.
const NARROW_MAX_ITEMS = 4;

// Segments after an entity resource that are not a record id.
const NON_ID_SEGMENTS = new Set(["create"]);
const NUMERIC_REGEX = /^\d+$/;

// PatientShow's tabs are positional: Informationen 0, Maßnahmen 1.
const PATIENT_PROCEDURES_TAB_INDEX = 1;
const PROCEDURE_SHOW_ROUTE = "/Procedure/:procedureId/show";

const ICONS_MAP = {
  start: <Icon type="rocket" size="small" />,
  Organization: <Icon type="home" size="small" />,
  Practitioner: <Icon type="practitioner" size="small" />,
  Patient: <Icon type="patient" size="small" />,
  Location: <Icon type="location" size="small" />,
  Device: <Icon type="device" size="small" />,
  EntityDetail: <Icon type="entity" size="small" />,
  Invitation: <Email fontSize="small" />,
  LearningGrant: <School fontSize="small" />,
  LearningAssignment: <AssignmentInd fontSize="small" />,
  LearningProgress: <Assessment fontSize="small" />,
  "salesforce-dashboard": <Assessment fontSize="small" />,
  "salesforce-records": <Assessment fontSize="small" />,
  "integration-settings": <Settings fontSize="small" />,
  "salesforce-integration": <Settings fontSize="small" />,
  "marketing-delivery": <ForwardToInboxOutlined fontSize="small" />,
  team: <GroupsOutlined fontSize="small" />,
  integrations: <Settings fontSize="small" />,
  account: <AccountCircleOutlined fontSize="small" />,
  "change-password": <LockOutlined fontSize="small" />,
  onboarding: <RocketLaunch fontSize="small" />,
} as const;

type ResourceType = Exclude<keyof typeof ICONS_MAP, "start" | "EntityDetail">;

// The resources with a show view, whose records the trail names.
const ENTITY_RESOURCES = ["Organization", "Practitioner", "Patient", "Location", "Device"] as const;
type EntityResource = (typeof ENTITY_RESOURCES)[number];

const isEntityResource = (segment: string): segment is EntityResource =>
  (ENTITY_RESOURCES as readonly string[]).includes(segment);

// Singular labels for a record that has no name of its own.
const ENTITY_LABEL_KEYS: Record<EntityResource, string> = {
  Organization: "organization",
  Practitioner: "Practitioner.title",
  Patient: "Patient.title",
  Location: "Location.title",
  Device: "Device.title",
};

// Injected into the last crumb by BreadcrumbsNav: the current page is plain text, never a link.
interface CurrentProps {
  current?: boolean;
}

interface BreadcrumbItemProps extends CurrentProps {
  icon: React.ReactNode;
  children: React.ReactNode;
}

// FIXME: https://media4care.atlassian.net/browse/NG-8268
// A name is cut at maxWidth; its tooltip shows it in full.
const BreadcrumbItem = ({ icon, current, children }: BreadcrumbItemProps): JSX.Element => {
  const label = (
    <Box
      component="span"
      aria-current={current ? "page" : undefined}
      sx={{
        marginLeft: space.xxs,
        maxWidth: maxWidth.xs,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {icon}
      {typeof children === "string" ? <Tooltip title={children}>{label}</Tooltip> : label}
    </Box>
  );
};

interface CrumbProps extends CurrentProps {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Crumb = ({ to, icon, current, children }: CrumbProps): JSX.Element =>
  current ? (
    <BreadcrumbItem icon={icon} current>
      {children}
    </BreadcrumbItem>
  ) : (
    <Link underline="hover" color="inherit" component={RouterLink} to={to}>
      <BreadcrumbItem icon={icon}>{children}</BreadcrumbItem>
    </Link>
  );

// A record's tabs live behind its show segment (/Practitioner/<id>/show/<tab>) and do not change the trail.
const recordPathSegments = (pathname: string): string[] => {
  const segments = pathname.split("/").filter(Boolean);
  const showIndex = segments.indexOf("show");
  return showIndex === -1 ? segments : segments.slice(0, showIndex);
};

const isResource = (segment: string): segment is ResourceType =>
  segment in ICONS_MAP && segment !== "start" && segment !== "EntityDetail";

interface ResourceCrumbProps extends CurrentProps {
  segment: ResourceType;
}

const ResourceCrumb = ({ segment, current }: ResourceCrumbProps): JSX.Element => {
  const translate = useTranslate();
  // The location list is "Standorte" whichever location the filter selects.
  const label = segment === "Location" ? translate("resources.LocationSites") : translate(`resources.${segment}`);

  return (
    <Crumb to={`/${segment}`} icon={ICONS_MAP[segment]} current={current}>
      {label}
    </Crumb>
  );
};

interface EntityCrumbProps extends CurrentProps {
  segment: string;
  resource: EntityResource;
}

const EntityCrumb = ({ segment, resource, current }: EntityCrumbProps): JSX.Element => {
  const translate = useTranslate();
  const { data, isLoading, error } = useGetOne<EntityData>(resource, { id: segment });

  return (
    <Crumb to={`/${resource}/${segment}/show`} icon={ICONS_MAP.EntityDetail} current={current}>
      {isLoading && !error ? "..." : getDisplayName(data, resource, translate)}
    </Crumb>
  );
};

interface EntityData {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  patientNumber?: string;
  manufacturer?: string;
  model?: string;
}

// A name the data provider fills with the record's id is no name.
const ownName = (data: EntityData | undefined): string => {
  const name = formatName(data);
  return name && name !== data?.id ? name : "";
};

// The record's name, never its id: what the resource has instead, then the singular label.
const getDisplayName = (
  data: EntityData | undefined,
  resource: EntityResource,
  translate: (key: string) => string
): string => {
  const fallback =
    resource === "Patient"
      ? data?.patientNumber
      : resource === "Device"
        ? [data?.manufacturer, data?.model].filter(Boolean).join(" ")
        : "";
  return ownName(data) || fallback || translate(ENTITY_LABEL_KEYS[resource]);
};

interface ProcedureTrail {
  isLoading: boolean;
  patientId?: string;
  patientName: string;
  title: string;
}

const useProcedureTrail = (procedureId: string | undefined): ProcedureTrail => {
  const translate = useTranslate();
  const { search } = useLocation();
  const requestedPatientId = new URLSearchParams(search).get(PROCEDURE_CLIENT_QUERY_PARAM);
  const { data, isLoading, error } = useGetOne<ProcedureRecord>(
    "Procedure",
    { id: procedureId ?? "" },
    { enabled: !!procedureId }
  );
  const patients = (data?.procedureParticipants?.nodes ?? []).flatMap(participant =>
    participant.patient ? [participant.patient] : []
  );
  // The client the user came from, if it is a participant; otherwise the first participant.
  const patient = patients.find(candidate => candidate.id === requestedPatientId) ?? patients[0];

  return {
    // The placeholder is for a request in flight; a failed lookup shows the fallbacks.
    isLoading: isLoading && !error,
    patientId: patient?.id,
    patientName: formatName(patient?.user) || patient?.patientNumber || translate("resources.Patient"),
    title: data?.codeDisplay || translate("Procedure.title"),
  };
};

const ProcedureClientCrumb = ({
  patientId,
  current,
  children,
}: CurrentProps & { patientId: string; children: React.ReactNode }) => (
  <Crumb to={`/Patient/${patientId}/show`} icon={ICONS_MAP.EntityDetail} current={current}>
    {children}
  </Crumb>
);

const ProcedureListCrumb = ({ patientId, current }: CurrentProps & { patientId: string }): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb
      to={`/Patient/${patientId}/show/${PATIENT_PROCEDURES_TAB_INDEX}`}
      icon={ICONS_MAP.EntityDetail}
      current={current}
    >
      {translate("tabs.procedures")}
    </Crumb>
  );
};

// The Maßnahme is always the current page; its client is kept by the crumbs before it.
const ProcedureCrumb = ({ title, current }: CurrentProps & { title: string }): JSX.Element => (
  <BreadcrumbItem icon={ICONS_MAP.EntityDetail} current={current}>
    {title}
  </BreadcrumbItem>
);

const CustomerServiceOrganizationsCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb to={CUSTOMER_SERVICE_ORGANIZATIONS_ROUTE} icon={ICONS_MAP.Organization} current={current}>
      {translate("customerServiceOrganizations.title")}
    </Crumb>
  );
};

const CustomerServiceOrganizationCrumb = ({
  organizationId,
  current,
}: CurrentProps & { organizationId: string }): JSX.Element => {
  const translate = useTranslate();
  const { organizations, isLoading } = useCustomerServiceLearningOrganizations();
  const organization = organizations.find(candidate => candidate.id === organizationId);

  return (
    <Crumb to={customerServiceOrganizationPath(organizationId)} icon={ICONS_MAP.Organization} current={current}>
      {isLoading ? "..." : organization?.name || translate("organization")}
    </Crumb>
  );
};

// CRM pages are custom routes: the CRM, then the object's list. A record shows
// its own title in the page header.
const CrmCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb to={CRM_ROUTE} icon={<HeadsetMicOutlined fontSize="small" />} current={current}>
      {translate("crm.title")}
    </Crumb>
  );
};

const MarketingCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb to={MARKETING_ROUTE} icon={<CampaignOutlined fontSize="small" />} current={current}>
      {translate("marketing.title")}
    </Crumb>
  );
};

// The Finance section has no page of its own: it links to the first finance page the user can open.
const FinanceSectionCrumb = ({ to, current }: CurrentProps & { to?: string }): JSX.Element => {
  const translate = useTranslate();
  const icon = <NotificationsOutlined fontSize="small" />;
  const label = translate("financeNotifications.section");
  return to ? (
    <Crumb to={to} icon={icon} current={current}>
      {label}
    </Crumb>
  ) : (
    <BreadcrumbItem icon={icon} current={current}>
      {label}
    </BreadcrumbItem>
  );
};

// Like Finance, the Logistics section has no page of its own: it links to the first logistics page the user can open.
const LogisticsSectionCrumb = ({ to, current }: CurrentProps & { to?: string }): JSX.Element => {
  const translate = useTranslate();
  const icon = <Inventory2Outlined fontSize="small" />;
  const label = translate("logistics.section");
  return to ? (
    <Crumb to={to} icon={icon} current={current}>
      {label}
    </Crumb>
  ) : (
    <BreadcrumbItem icon={icon} current={current}>
      {label}
    </BreadcrumbItem>
  );
};

const LogisticsInventoryCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb to={LOGISTICS_INVENTORY_ROUTE} icon={null} current={current}>
      {translate("logistics.inventory.title")}
    </Crumb>
  );
};

// The unit's page names itself through the breadcrumb title; the unit label stands in until it does.
const LogisticsInventoryItemCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  const title = useBreadcrumbTitle();
  return (
    <BreadcrumbItem icon={null} current={current}>
      {title || translate("logistics.item.sections.unit")}
    </BreadcrumbItem>
  );
};

const FinanceInboxCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb to={FINANCE_NOTIFICATIONS_ROUTE} icon={null} current={current}>
      {translate("financeNotifications.title")}
    </Crumb>
  );
};

const AnalyticsCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb to={ANALYTICS_ROUTE} icon={<Assessment fontSize="small" />} current={current}>
      {translate("analytics.title")}
    </Crumb>
  );
};

const AnalyticsPageCrumb = ({ page, current }: CurrentProps & { page: AnalyticsPage }): JSX.Element => {
  const translate = useTranslate();
  return (
    <BreadcrumbItem icon={page.icon} current={current}>
      {translate(page.labelKey)}
    </BreadcrumbItem>
  );
};

// The records behind the Salesforce dashboard's numbers: the dashboard is their parent page.
const SalesforceDashboardCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <Crumb to={SALESFORCE_DASHBOARD_ROUTE} icon={ICONS_MAP["salesforce-dashboard"]} current={current}>
      {translate("salesforceDashboard.title")}
    </Crumb>
  );
};

const CrmCallsCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <BreadcrumbItem icon={<PhoneOutlined fontSize="small" />} current={current}>
      {translate("crm.calls.overline")}
    </BreadcrumbItem>
  );
};

const CrmDuplicatesCrumb = ({ current }: CurrentProps): JSX.Element => {
  const translate = useTranslate();
  return (
    <BreadcrumbItem icon={<CompareArrowsOutlined fontSize="small" />} current={current}>
      {translate("crm.duplicates.title")}
    </BreadcrumbItem>
  );
};

// The record page names itself through the breadcrumb title; the object's singular label stands in until it does.
const CrmRecordCrumb = ({ object, current }: CurrentProps & { object: CrmObject }): JSX.Element => {
  const labels = useCrmLabels();
  const title = useBreadcrumbTitle();
  return (
    <BreadcrumbItem icon={<CrmObjectIcon object={object} size="small" />} current={current}>
      {title || labels.object(object)}
    </BreadcrumbItem>
  );
};

const CrmObjectCrumb = ({ object, current }: CurrentProps & { object: CrmObject }): JSX.Element => {
  const labels = useCrmLabels();
  return (
    <Crumb to={crmListPath(object)} icon={<CrmObjectIcon object={object} size="small" />} current={current}>
      {labels.object(object, true)}
    </Crumb>
  );
};

export const BreadcrumbsNav = (): JSX.Element => {
  const location = useLocation();
  const translate = useTranslate();
  const isNarrow = useMediaQuery(useTheme().breakpoints.down("sm"));
  const { canAccess: canOpenFacilities } = useCanAccess({
    action: "list",
    resource: CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE,
  });
  // Staff without the Marketing menu reach the web orders from the CRM.
  const { canAccess: canOpenMarketing } = useCanAccess({ action: "list", resource: ResourceName.Marketing });
  // The facility pages are custom routes; their segments are not react-admin resources.
  const facilitiesMatch = matchPath(`${CUSTOMER_SERVICE_ORGANIZATIONS_ROUTE}/*`, location.pathname);
  const customerServiceOrganizationMatch = canOpenFacilities
    ? matchPath(`${CUSTOMER_SERVICE_ORGANIZATION_ROUTE}/*`, location.pathname)
    : null;
  const customerServiceOrganizationId = customerServiceOrganizationMatch?.params.organizationId;

  const crmMatch =
    location.pathname === CRM_ROUTE ? { params: {} } : matchPath(`${CRM_ROUTE}/:objectSlug/*`, location.pathname);
  const crmObject = crmObjectFromSlug((crmMatch?.params as { objectSlug?: string } | undefined)?.objectSlug);
  const isCrmRecord = !!crmObject && !!matchPath(CRM_RECORD_ROUTE, location.pathname);

  const { canAccess: canOpenFinancePages } = useCanAccessResources({
    action: "list",
    resources: FINANCE_PAGES.map(page => page.resource),
  });
  const financeEntryRoute = FINANCE_PAGES.find(page => canOpenFinancePages?.[page.resource] === true)?.to;

  const { canAccess: canOpenLogisticsPages } = useCanAccessResources({
    action: "list",
    resources: LOGISTICS_PAGES.map(page => page.resource),
  });
  const logisticsEntryRoute = LOGISTICS_PAGES.find(page => canOpenLogisticsPages?.[page.resource] === true)?.to;

  const analyticsMatch = matchPath(`${ANALYTICS_ROUTE}/*`, location.pathname);
  const analyticsSegment = analyticsMatch?.params["*"]?.split("/")[0];
  const analyticsPage = ANALYTICS_PAGES.find(page => page.segment === analyticsSegment);

  const salesforceRecordsMatch = matchPath(SALESFORCE_RECORDS_ROUTE, location.pathname);
  const crmCallsMatch = matchPath(CRM_CALLS_ROUTE, location.pathname);
  const crmDuplicatesMatch = matchPath(CRM_DUPLICATES_ROUTE, location.pathname);

  const marketingMatch = matchPath(`${MARKETING_ROUTE}/*`, location.pathname);

  const financeMatch = matchPath(`${FINANCE_NOTIFICATIONS_ROUTE}/*`, location.pathname);
  const customerMatchMatch = matchPath(BILLWERK_CUSTOMER_MATCH_ROUTE, location.pathname);
  const planVariantsMatch = matchPath(BILLWERK_PLAN_VARIANTS_ROUTE, location.pathname);
  const financeOverviewMatch = matchPath(FINANCE_OVERVIEW_ROUTE, location.pathname);
  const logisticsMatch = matchPath(`${LOGISTICS_INVENTORY_ROUTE}/*`, location.pathname);
  const logisticsItemMatch = matchPath(LOGISTICS_INVENTORY_ITEM_ROUTE, location.pathname);

  // Procedures have only a show view and no list page; the trail runs through their client's Maßnahmen tab.
  const procedureMatch = matchPath(PROCEDURE_SHOW_ROUTE, location.pathname);
  const procedureId = procedureMatch?.params.procedureId;
  const procedureTrail = useProcedureTrail(procedureId);

  const pathSegments =
    facilitiesMatch ||
    procedureMatch ||
    crmMatch ||
    marketingMatch ||
    analyticsMatch ||
    financeMatch ||
    customerMatchMatch ||
    planVariantsMatch ||
    financeOverviewMatch ||
    logisticsMatch
      ? []
      : recordPathSegments(location.pathname);

  // The last crumb is the page the user is on: plain text, not a link.
  const crumbs = React.Children.toArray([
    <Crumb key="start" to="/" icon={ICONS_MAP.start}>
      {translate("start")}
    </Crumb>,
    facilitiesMatch && <CustomerServiceOrganizationsCrumb key="customer-service-organizations" />,
    customerServiceOrganizationId && (
      <CustomerServiceOrganizationCrumb
        key="customer-service-organization"
        organizationId={customerServiceOrganizationId}
      />
    ),
    crmMatch && <CrmCrumb key="crm" />,
    marketingMatch && canOpenMarketing === true && <MarketingCrumb key="marketing" />,
    location.pathname === WEB_ORDERS_ROUTE && (
      <BreadcrumbItem key="web-orders" icon={<ShoppingCartOutlined fontSize="small" />}>
        {translate("marketing.webOrders.title")}
      </BreadcrumbItem>
    ),
    (financeMatch || customerMatchMatch || planVariantsMatch || financeOverviewMatch) && (
      <FinanceSectionCrumb key="finance" to={financeEntryRoute} />
    ),
    financeOverviewMatch && (
      <BreadcrumbItem key="finance-overview" icon={null}>
        {translate("financeOverview.title")}
      </BreadcrumbItem>
    ),
    financeMatch && <FinanceInboxCrumb key="finance-notifications" />,
    logisticsMatch && <LogisticsSectionCrumb key="logistics" to={logisticsEntryRoute} />,
    logisticsMatch && <LogisticsInventoryCrumb key="logistics-inventory" />,
    logisticsItemMatch && <LogisticsInventoryItemCrumb key="logistics-inventory-item" />,
    customerMatchMatch && (
      <BreadcrumbItem key="billwerk-customer-match" icon={null}>
        {translate("billwerkCustomerMatch.title")}
      </BreadcrumbItem>
    ),
    planVariantsMatch && (
      <BreadcrumbItem key="billwerk-plan-variants" icon={null}>
        {translate("billwerkPlanVariants.title")}
      </BreadcrumbItem>
    ),
    analyticsMatch && <AnalyticsCrumb key="analytics" />,
    analyticsPage && <AnalyticsPageCrumb key="analytics-page" page={analyticsPage} />,
    crmCallsMatch && <CrmCallsCrumb key="crm-calls" />,
    crmDuplicatesMatch && <CrmDuplicatesCrumb key="crm-duplicates" />,
    crmObject && <CrmObjectCrumb key="crm-object" object={crmObject} />,
    crmObject && isCrmRecord && <CrmRecordCrumb key="crm-record" object={crmObject} />,
    procedureId && <ResourceCrumb key="procedure-patients" segment="Patient" />,
    procedureId && procedureTrail.isLoading && (
      <BreadcrumbItem key="procedure-loading" icon={ICONS_MAP.EntityDetail}>
        ...
      </BreadcrumbItem>
    ),
    procedureId && !procedureTrail.isLoading && procedureTrail.patientId && (
      <ProcedureClientCrumb key="procedure-client" patientId={procedureTrail.patientId}>
        {procedureTrail.patientName}
      </ProcedureClientCrumb>
    ),
    procedureId && !procedureTrail.isLoading && procedureTrail.patientId && (
      <ProcedureListCrumb key="procedure-list" patientId={procedureTrail.patientId} />
    ),
    procedureId && !procedureTrail.isLoading && <ProcedureCrumb key="procedure" title={procedureTrail.title} />,
    salesforceRecordsMatch && <SalesforceDashboardCrumb key="salesforce-dashboard" />,
    pathSegments.map((segment, idx) => {
      const prevSegment = idx > 0 ? pathSegments[idx - 1] : undefined;

      // Resource segment (e.g., "Patient", "Organization")
      if (isResource(segment)) {
        return <ResourceCrumb key={segment} segment={segment} />;
      }

      // Record id following a resource with a show view; any other segment is not shown.
      if (
        prevSegment &&
        isEntityResource(prevSegment) &&
        !NON_ID_SEGMENTS.has(segment) &&
        !NUMERIC_REGEX.test(segment)
      ) {
        return <EntityCrumb key={`${prevSegment}-${segment}`} segment={segment} resource={prevSegment} />;
      }

      return null;
    }),
  ]).filter(React.isValidElement);
  const trail = crumbs.map((crumb, index) =>
    index === crumbs.length - 1
      ? React.cloneElement(crumb as React.ReactElement<CurrentProps>, { current: true })
      : crumb
  );

  return (
    <Breadcrumbs
      aria-label={translate("breadcrumb")}
      // On narrow screens the middle of a long trail collapses: Start, an expander, the parent and the current page.
      maxItems={isNarrow ? NARROW_MAX_ITEMS : undefined}
      itemsBeforeCollapse={1}
      itemsAfterCollapse={2}
      expandText={translate("breadcrumbExpand")}
      separator={<Icon type="chevron-right-filled" size="medium" />}
      sx={{
        minWidth: 0,
        overflowX: "auto",
        "& .MuiBreadcrumbs-ol": {
          alignItems: "center",
          flexWrap: "nowrap",
          width: "max-content",
        },
        "& .MuiBreadcrumbs-li": {
          display: "flex",
          alignItems: "center",
        },
        "& .MuiBreadcrumbs-separator": {
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      {trail}
    </Breadcrumbs>
  );
};
